import { apiFetch } from './client';

export type SurveyKind = 'voice' | 'sms';
export type AnswerKind = 'dtmf_single' | 'dtmf_multi' | 'sms_text' | 'sms_dtmf';

export interface SurveyRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  ref_number: string;
  name: string;
  description: string | null;
  kind: SurveyKind;
  points_on_complete: number;
  active: boolean;
  question_count: number;
  response_count: number;
  created_at: string;
  updated_at: string;
}

export interface SurveyQuestionRow {
  id: string;
  survey_id: string;
  sort_order: number;
  question_text: string;
  recording_id: string | null;
  answer_kind: AnswerKind;
  choices: unknown[];
  required: boolean;
  created_at: string;
}

export interface SurveyResponseRow {
  id: string;
  caller_id: string | null;
  started_at: string;
  completed_at: string | null;
  answers: Record<string, unknown>;
  test_mode: boolean;
}

export interface CreateSurveyBody {
  name: string;
  description?: string | null;
  kind?: SurveyKind;
  points_on_complete?: number;
  customer_id?: string;
}

export interface PatchSurveyBody {
  name?: string;
  description?: string | null;
  kind?: SurveyKind;
  points_on_complete?: number;
  active?: boolean;
}

export interface CreateQuestionBody {
  question_text: string;
  answer_kind: AnswerKind;
  choices?: unknown[];
  recording_id?: string | null;
  sort_order?: number;
  required?: boolean;
}

export const surveys = {
  list: (p: { search?: string; limit?: number; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.search) q.set('search', p.search);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.customer_id) q.set('customer_id', p.customer_id);
    return apiFetch<{ rows: SurveyRow[] }>(`/api/surveys?${q}`);
  },
  get: (id: string) => apiFetch<SurveyRow>(`/api/surveys/${id}`),
  create: (body: CreateSurveyBody) =>
    apiFetch<SurveyRow>('/api/surveys', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: PatchSurveyBody) =>
    apiFetch<SurveyRow>(`/api/surveys/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) =>
    apiFetch<void>(`/api/surveys/${id}`, { method: 'DELETE' }),

  listQuestions: (surveyId: string) =>
    apiFetch<{ rows: SurveyQuestionRow[] }>(`/api/surveys/${surveyId}/questions`),
  createQuestion: (surveyId: string, body: CreateQuestionBody) =>
    apiFetch<SurveyQuestionRow>(`/api/surveys/${surveyId}/questions`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateQuestion: (id: string, body: Partial<{
    question_text: string; answer_kind: AnswerKind; choices: unknown[];
    recording_id: string | null; sort_order: number; required: boolean;
  }>) =>
    apiFetch<SurveyQuestionRow>(`/api/survey-questions/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  removeQuestion: (id: string) =>
    apiFetch<void>(`/api/survey-questions/${id}`, { method: 'DELETE' }),

  listResponses: (surveyId: string, p: { limit?: number; completed?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (p.limit) q.set('limit', String(p.limit));
    if (p.completed) q.set('completed', 'true');
    return apiFetch<{ rows: SurveyResponseRow[] }>(`/api/surveys/${surveyId}/responses?${q}`);
  },
};
